import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Bot, UserRound, Users } from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import EmptyStateCard from "../../EmptyStateCard";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { saasApi } from "../shared/saasApi";
import { IdentityFilter, SaasIdentity } from "../shared/saasTypes";
import { formatDate, formatNumber, formatRelative, PAGE_SIZE } from "../shared/saasFormat";
import { KeyValue, ListSkeleton, ListToolbar, MetaTag, PermissionBadge, ToolbarSearch } from "../shared/SaasUi";
import { WorkspaceContext } from "./workspaceContext";

type IdentityView = "all" | "privileged" | "dormant" | "outside" | "twoFactor" | "bots";

const VIEWS: { value: IdentityView; label: string; githubOnly?: boolean }[] = [
    { value: "all", label: "All identities" },
    { value: "privileged", label: "Privileged" },
    { value: "dormant", label: "Dormant" },
    { value: "outside", label: "Outside collaborators", githubOnly: true },
    { value: "twoFactor", label: "2FA disabled" },
    { value: "bots", label: "Bots" },
];

const VIEW_FILTERS: Record<IdentityView, Partial<IdentityFilter>> = {
    all: {},
    privileged: { privileged: true },
    dormant: { dormant: true },
    outside: { outsideCollaborator: true },
    twoFactor: { twoFactorDisabled: true },
    bots: { identityType: "BOT" },
};

export default function IdentitiesTab({ context }: { context: WorkspaceContext }) {
    const [searchInput, setSearchInput] = useState("");
    const search = useDebouncedValue(searchInput, 350) as string;
    const [view, setView] = useState<IdentityView>("all");
    const [sort, setSort] = useState<NonNullable<IdentityFilter["sort"]>>("privileged");
    const [page, setPage] = useState(1);

    useEffect(() => setPage(1), [search, view, sort, context.connectionId]);

    const filter: IdentityFilter = { search: search || undefined, ...VIEW_FILTERS[view], sort };
    const query = useQuery(
        ["saas-identities", context.provider, context.tenantName, context.connectionId, context.range, filter, page],
        () => saasApi.identities(context.provider, context.tenantName, context.connectionId, context.range, filter, page - 1, PAGE_SIZE),
        { keepPreviousData: true },
    );
    const result = query.data;

    return (
        <div className="space-y-2">
            <ListToolbar page={page} totalPages={result?.totalPages ?? 1} onPageChange={setPage} total={result?.totalItems ?? 0} pageSize={PAGE_SIZE}
                         label="Identity pages">
                <ToolbarSearch value={searchInput} onChange={setSearchInput} placeholder="Search login, name or email..." />
                <Select value={view} onValueChange={(value) => setView(value as IdentityView)}>
                    <SelectTrigger className="saas-control w-52" aria-label="Identity filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {VIEWS.filter((item) => !item.githubOnly || context.provider === "github").map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                                {item.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={sort} onValueChange={(value) => setSort(value as NonNullable<IdentityFilter["sort"]>)}>
                    <SelectTrigger className="saas-control w-48" aria-label="Sort identities">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="privileged">Most privileged</SelectItem>
                        <SelectItem value="grants">Most access</SelectItem>
                        <SelectItem value="lastActivity">Recently active</SelectItem>
                        <SelectItem value="login">Login A–Z</SelectItem>
                    </SelectContent>
                </Select>
            </ListToolbar>

            {query.isLoading ? (
                <ListSkeleton />
            ) : !result || result.items.length === 0 ? (
                <EmptyStateCard icon={Users} title="No identities found" description="Adjust the filters or wait for the first sync to finish."
                                className="cloud-card-surface border-dashed rounded-2xl" />
            ) : (
                result.items.map((identity) => (
                    <IdentityCard key={`${identity.connectionId}-${identity.id}`} identity={identity}
                                  onOpen={() => context.openIdentity(identity.connectionId, identity.login)} />
                ))
            )}
        </div>
    );
}

function IdentityCard({ identity, onOpen }: { identity: SaasIdentity; onOpen: () => void }) {
    return (
        <Card className="cloud-list-card cursor-pointer rounded-2xl" onClick={onOpen} data-testid={`card-saas-identity-${identity.id}`}>
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {identity.avatarUrl ? (
                            <img src={identity.avatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : identity.identityType === "BOT" ? (
                            <Bot className="w-4 h-4 text-primary" />
                        ) : (
                            <UserRound className="w-4 h-4 text-primary" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <h3 className="font-semibold text-sm truncate">{identity.login}</h3>
                            {identity.displayName && identity.displayName !== identity.login && (
                                <span className="text-xs text-muted-foreground truncate">{identity.displayName}</span>
                            )}
                            <PermissionBadge level={identity.highestPermission} />
                            {identity.role && <MetaTag>{identity.role}</MetaTag>}
                            {identity.identityType === "BOT" && <MetaTag>Bot</MetaTag>}
                            {identity.siteAdmin && <MetaTag className="!text-red-700 dark:!text-red-300">Site admin</MetaTag>}
                            {identity.outsideCollaborator && <MetaTag className="!text-amber-700 dark:!text-amber-300">Outside collaborator</MetaTag>}
                            {identity.dormant && <MetaTag className="!text-amber-700 dark:!text-amber-300">Dormant</MetaTag>}
                            {identity.twoFactorEnabled === false && <MetaTag className="!text-red-700 dark:!text-red-300">2FA disabled</MetaTag>}
                            {identity.state && !["active"].includes(identity.state.toLowerCase()) && <MetaTag>{identity.state}</MetaTag>}
                        </div>
                        {identity.email && <p className="text-xs text-muted-foreground truncate">{identity.email}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 text-right">
                        <span className="text-sm font-semibold">{formatNumber(identity.eventCount)}</span>
                        <span className="text-[11px] text-muted-foreground">events</span>
                        {identity.highRiskEventCount > 0 && (
                            <span className="text-[11px] font-medium text-red-600 dark:text-red-400">{formatNumber(identity.highRiskEventCount)} high risk</span>
                        )}
                    </div>
                </div>
                <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KeyValue label="Access">{`${formatNumber(identity.grantCount)} grants · ${formatNumber(identity.privilegedGrantCount)} privileged`}</KeyValue>
                    <KeyValue label="Last activity">{formatRelative(identity.lastActivityAt, "No activity recorded")}</KeyValue>
                    <KeyValue label="Last sign-in">{formatRelative(identity.lastSignInAt, "—")}</KeyValue>
                    <KeyValue label="Account created">{formatDate(identity.platformCreatedAt)}</KeyValue>
                </div>
            </CardContent>
        </Card>
    );
}
